import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  TextField,
  Button,
  Divider,
  CircularProgress,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../theme/ThemeModeProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  selectLanguage,
  selectSidebarCollapsed,
} from "store/slices/app/appSelector";
import { LanguageEnum } from "store/slices/app/appTypes";
import { setLanguage, setToastMessage } from "store/slices/app/appSlice";
import {
  selectUserDb,
  selectUserApp,
} from "store/slices/session/sessionSelector";
import { useGetUserCompaniesQuery } from "store/apis/companyApi";
import {
  useUpdateUserMutation,
  useGetUserByEmailQuery,
} from "store/apis/usersApi";
import { Company } from "types/CompanyTypes";
import { getAuth } from "firebase/auth";
import { uploadUserImage, getUserImageUrl } from "../firebase/firebaseStorage";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { setAvatar } from "../store/slices/session/sessionSlice";
import * as Sentry from "@sentry/react";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import AppVersion from "../components/settings/AppVersion";

const Settings: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const language = useSelector(selectLanguage);
  const { mode, setMode } = useThemeMode();
  const dispatch = useDispatch();
  const userDb = useSelector(selectUserDb);
  const sessionUser = useSelector(selectUserApp);
  const isAdmin = userDb?.role === "ADMIN";
  const { data: companiesResponse } = useGetUserCompaniesQuery();
  const companiesData = companiesResponse?.data || [];
  const [
    updateUser,
    { isLoading: isUpdating, error: updateError, data: updateData },
  ] = useUpdateUserMutation();

  // Setup query to refresh user data after update
  const { refetch: refetchUserData } = useGetUserByEmailQuery(
    sessionUser?.email || "",
    {
      skip: !sessionUser?.email,
    }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    active: true,
  });

  // Initialize form with user data
  useEffect(() => {
    if (userDb) {
      setUserData({
        firstName: userDb.firstName || "",
        lastName: userDb.lastName || "",
        email: userDb.email || "",
        phone: userDb.phone || "",
        role: userDb.role || "",
        active: userDb.active !== undefined ? userDb.active : true,
      });

      // Load user profile image
      if (userDb.firebaseUid) {
        getUserImageUrl(userDb.firebaseUid)
          .then((url) => {
            setImageUrl(url);
          })
          .catch((error) => {
            console.error("Error loading profile image:", error);
          });
      }
    }
  }, [userDb]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
    setIsEditing(true); // Enable editing mode when user changes any field
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!userData.firstName.trim()) {
      errors.firstName = t("validation.required");
      isValid = false;
    }

    if (!userData.lastName.trim()) {
      errors.lastName = t("validation.required");
      isValid = false;
    }

    if (!userData.email.trim()) {
      errors.email = t("validation.required");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      errors.email = t("validation.invalidEmail");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!userDb?.firebaseUid) return;

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const file = event.target.files[0];
    setUploading(true);

    try {
      // Upload the image and get the URL
      const newImageUrl = await uploadUserImage(file, userDb.firebaseUid);

      if (newImageUrl) {
        setImageUrl(newImageUrl);

        // If this is the current user's avatar, update Redux with the URL
        if (sessionUser?.uid === userDb.firebaseUid) {
          dispatch(setAvatar(newImageUrl));
        }

        dispatch(
          setToastMessage({
            message: t("users.notifications.uploadImageSuccess"),
            severity: "success",
          })
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error uploading image:", error);
      dispatch(
        setToastMessage({
          message: t("users.notifications.uploadImageError"),
          severity: "error",
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !userDb) return;

    // Update user using the mutation
    // Only send fields that are actually editable to avoid auth errors
    updateUser({
      id: userDb.id,
      updates: {
        firstName: userData.firstName,
        lastName: userData.lastName,
        // Don't send email as it's not editable and causes auth errors
        // email: userData.email,
        phone: userData.phone,
      },
    });
  };

  // Handle update success
  useEffect(() => {
    if (updateData) {
      dispatch(
        setToastMessage({
          message: t("users.notifications.updateSuccess"),
          severity: "success",
        })
      );
      setIsEditing(false);

      // Refresh user data to update other parts of the app
      if (sessionUser?.email) {
        refetchUserData();
      }
    }
  }, [updateData, dispatch, t, sessionUser?.email, refetchUserData]);

  // Handle update error
  useEffect(() => {
    if (updateError) {
      console.error("Error updating user:", updateError);
      dispatch(
        setToastMessage({
          message:
            updateError &&
            "data" in updateError &&
            typeof updateError.data === "object" &&
            updateError.data &&
            "message" in updateError.data
              ? String(updateError.data.message)
              : t("users.notifications.updateError"),
          severity: "error",
        })
      );
    }
  }, [updateError, dispatch, t]);

  // Reset form happens in useEffect when userDb changes

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h3" gutterBottom>
              {t("settings.title")}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 4,
            p: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                {t("settings.language")}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Select
                  value={language}
                  onChange={(event: SelectChangeEvent<LanguageEnum>) =>
                    dispatch(setLanguage(event.target.value as LanguageEnum))
                  }
                  variant="outlined"
                  sx={{
                    minWidth: 200,
                    color: theme.palette.text.primary,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.neutral[300],
                    },
                    "& .MuiSvgIcon-root": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <MenuItem value={LanguageEnum.EN}>
                    {t("languages.en")}
                  </MenuItem>
                  <MenuItem value={LanguageEnum.ES}>
                    {t("languages.es")}
                  </MenuItem>
                </Select>
              </Box>
            </Box>

            <Box>
              <Typography variant="h4" gutterBottom>
                {t("settings.theme")}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, value) => value && setMode(value)}
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.neutral[300],
                      textTransform: "none",
                      gap: 1,
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.getContrastText(
                          theme.palette.primary.main
                        ),
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="light">
                    <LightModeIcon fontSize="small" />
                    {t("settings.lightMode")}
                  </ToggleButton>
                  <ToggleButton value="dark">
                    <DarkModeIcon fontSize="small" />
                    {t("settings.darkMode")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" gutterBottom>
            {t("settings.profile")}
          </Typography>

          <Box sx={{ mt: 3 }}>
            {/* User Profile Header */}

            {/* Company Logos */}
            {companiesData && companiesData.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.profile.companies")}
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {companiesData.map((company: Company) => (
                    <Box
                      key={company.id}
                      sx={{
                        width: 120,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                          backgroundColor: theme.palette.grey[100],
                          borderRadius: 6,
                        }}
                      >
                        {company.logoUrl ? (
                          <img
                            src={company.logoUrl}
                            alt={company.nameCommercial}
                            style={{
                              width: "auto",
                              height: "auto",
                              maxWidth: "100%",
                              maxHeight: "100%",
                              borderRadius: 24,
                            }}
                          />
                        ) : company.imageBase64 ? (
                          <img
                            src={`data:image/png;base64,${company.imageBase64}`}
                            alt={company.nameCommercial}
                            style={{
                              width: "auto",
                              height: "auto",
                              maxWidth: "100%",
                              maxHeight: "100%",
                              borderRadius: 24,
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{ fontFamily: "Nunito" }}
                          >
                            {company.nameCommercial}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{
                          fontFamily: "Nunito",
                          fontWeight: 600,
                          color: theme.palette.grey[700],
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {company.nameCommercial}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* User Info Form */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                }}
              >
                {t("users.profile.yourData")}
              </Typography>

              <Grid container spacing={3}>
                {/* First Row - User Profile Image */}
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                >
                  <FormControl sx={{ width: "100%", maxWidth: 400 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: 24,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: theme.palette.grey[100],
                          overflow: "hidden",
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={userDb?.firstName || t("users.profileImage")}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <PhotoCamera
                            sx={{
                              color: theme.palette.grey[400],
                              fontSize: 40,
                            }}
                          />
                        )}
                      </Box>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: "none" }}
                        id="profile-image-upload"
                        disabled={uploading}
                      />
                      <label htmlFor="profile-image-upload">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={<PhotoCamera />}
                          disabled={uploading}
                          sx={{
                            borderRadius: 50,
                            textTransform: "none",
                            fontFamily: "Nunito",
                            borderColor: theme.palette.grey[200],
                            color: theme.palette.grey[700],
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          {uploading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress size={16} />
                              {t("loading")}
                            </Box>
                          ) : (
                            t(
                              imageUrl
                                ? "users.changeImage"
                                : "users.uploadImage"
                            )
                          )}
                        </Button>
                      </label>
                    </Box>
                  </FormControl>
                </Grid>

                {/* Second Row */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.firstName")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="firstName"
                      placeholder={t("users.firstName")}
                      value={userData.firstName}
                      onChange={handleInputChange}
                      error={!!formErrors.firstName}
                      helperText={formErrors.firstName}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.lastName")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="lastName"
                      placeholder={t("users.lastName")}
                      value={userData.lastName}
                      onChange={handleInputChange}
                      error={!!formErrors.lastName}
                      helperText={formErrors.lastName}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.email")}
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      name="email"
                      placeholder={t("users.email")}
                      value={userData.email}
                      disabled={true} // Email should not be editable
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.profile.phone")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="phone"
                      placeholder={t("users.profile.phone")}
                      value={userData.phone}
                      onChange={handleInputChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {/* Third Row - Role and Status (only visible for admins) */}
                {isAdmin && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Nunito",
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {t("users.role")}
                        </Typography>
                        <TextField
                          fullWidth
                          name="role"
                          value={userData.role}
                          disabled={true} // Role should not be editable in profile
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 50,
                              fontFamily: "Nunito",
                              fontSize: 16,
                              "& fieldset": {
                                borderColor: theme.palette.grey[200],
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Nunito",
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {t("users.status")}
                        </Typography>
                        <TextField
                          fullWidth
                          value={
                            userData.active
                              ? t("users.active")
                              : t("common.inactive")
                          }
                          disabled={true} // Status should not be editable in profile
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 50,
                              fontFamily: "Nunito",
                              fontSize: 16,
                              "& fieldset": {
                                borderColor: theme.palette.grey[200],
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Update Button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!isEditing || isUpdating}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.getContrastText(
                      theme.palette.grey[900]
                    ),
                    "&:hover": {
                      backgroundColor: theme.palette.grey[800],
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("users.updateUser")
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
          <AppVersion />
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
